import type { FeedbackKind } from "@/shared/api";

/**
 * The three things a reporter can be telling us, and how to ask about each.
 *
 * All the user-facing copy for the form lives here, in one table, for two
 * reasons. The obvious one is that the wording is the part most likely to be
 * revised. The real one is that this is the whole of the translation: the
 * reporter is an accountant who chose "Something isn't working", and the
 * developer receives an issue labeled `bug`. Neither of them has to learn the
 * other's vocabulary, and the mapping is one table on each side - this, and
 * `_KINDS` in `backend/app/feedback/issue.py`.
 *
 * The questions are deliberately concrete. "Describe the issue" gets a sentence
 * nobody can reproduce from; "What were you doing just before it went wrong?"
 * gets the click order.
 */
export interface FeedbackKindCopy {
  kind: FeedbackKind;
  /** The choice, as the reporter reads it. */
  label: string;
  /** Which one to pick, for someone unsure. */
  hint: string;
  /** The single-line question. Becomes the issue title. */
  summaryLabel: string;
  summaryPlaceholder: string;
  /** The long question. */
  detailLabel: string;
  detailHint: string;
  detailPlaceholder: string;
  /** The submit button, phrased as what it does. */
  submitLabel: string;
}

export const FEEDBACK_KINDS: FeedbackKindCopy[] = [
  {
    kind: "bug",
    label: "Something isn't working",
    hint: "A button does nothing, a number looks wrong, or the page shows an error.",
    summaryLabel: "In one line, what went wrong?",
    summaryPlaceholder: "The download button does nothing on the review screen",
    detailLabel: "What were you doing just before it went wrong?",
    detailHint:
      "Step by step is ideal, but anything helps - the ordinary details are the useful ones.",
    detailPlaceholder:
      "I uploaded both files, clicked Run, waited about a minute, then clicked Download and nothing happened.",
    submitLabel: "Send this to the team",
  },
  {
    kind: "idea",
    label: "I have an idea",
    hint: "Something that would make the close faster, clearer or less fiddly.",
    summaryLabel: "In one line, what would you like?",
    summaryPlaceholder: "Remember the landing rate I used last month",
    detailLabel: "What would it let you do?",
    detailHint: "The problem it solves matters more than how it should look.",
    detailPlaceholder:
      "I type the same rate every month and I have to look it up first, so it is easy to get wrong.",
    submitLabel: "Send this idea",
  },
  {
    kind: "question",
    label: "I have a question",
    hint: "Something about the app is unclear, or you are not sure a number is right.",
    summaryLabel: "In one line, what would you like to know?",
    summaryPlaceholder: "Why does PO 4417 show a variance when the totals match?",
    detailLabel: "What are you looking at, and what is confusing?",
    detailHint: "Naming the screen and the number is usually enough for someone to check it.",
    detailPlaceholder:
      "On the reconciliation screen the customs column is red for PO 4417, but the two totals underneath look the same to me.",
    submitLabel: "Send this question",
  },
];

/** The question asked of a bug only - the gap from `detail` is the reproduction. */
export const EXPECTED_LABEL = "What did you expect to happen instead?";

export const EXPECTED_HINT = 'Even "I expected a spreadsheet to download" helps.';

/**
 * Looks up the copy for a kind.
 *
 * @param kind - The chosen kind.
 * @returns Its copy. Falls back to the first entry, which cannot happen through
 *   the UI but keeps this total rather than optional at every call site.
 */
export function copyFor(kind: FeedbackKind): FeedbackKindCopy {
  return FEEDBACK_KINDS.find((entry) => entry.kind === kind) ?? FEEDBACK_KINDS[0]!;
}
